import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePrice } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Price as RxPrice } from "../collections/price";
import type { RxDBCollections } from "../provider";
import {
  SyncPricePullDocument,
  type WholeSyncablePriceFragment,
} from "./__generated__/price.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePrice = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPrice>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPricePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<RxPrice, SyncablePrice, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceToRxPrice,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price",
  });
};

export const mapGqlPriceToRxPrice = (
  price: WholeSyncablePriceFragment,
): WithDeleted<RxPrice> => ({
  id: price.id,
  value: price.value,
  priceListId: price.priceListId,
  itemId: price.itemId,
  tariffId: price.tariffId,
  revenueDepartmentId: price.revenueDepartmentId,
  meta: {
    createdAt: price.meta.createdAt,
    createdByUserId: price.meta.createdByUserId,
    updatedAt: price.meta.updatedAt,
    updatedByUserId: price.meta.updatedByUserId,
  },
  _deleted: price.meta.deleted,
});

gql`
  fragment WholeSyncablePrice on SyncablePrice {
    id
    value
    priceListId
    itemId
    tariffId
    revenueDepartmentId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPricePull($input: PullPriceInput!) {
    pullPrice(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePrice
      }
    }
  }
`;
